
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import { API_BASE_URL } from '../../constants/api';

export default function CustomerSupportPage() {
  const [selectedTicket, setSelectedTicket] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    category: '',
    orderId: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const supportCategories = [
    { id: 'booking', name: 'Booking Issues', icon: 'ri-calendar-line' },
    { id: 'payment', name: 'Payment Problems', icon: 'ri-money-rupee-circle-line' },
    { id: 'service', name: 'Service Quality', icon: 'ri-star-line' },
    { id: 'refund', name: 'Refund Request', icon: 'ri-refund-line' },
    { id: 'account', name: 'Account Issues', icon: 'ri-user-line' },
    { id: 'other', name: 'Other', icon: 'ri-question-line' }
  ];

  const commonIssues = [
    {
      id: 'booking-cancel',
      title: 'How to cancel my booking?',
      answer:
        'You can cancel your booking up to 2 hours before the scheduled time through the app or by calling our support team. Cancellations made within 2 hours may incur a small fee.'
    },
    {
      id: 'payment-failed',
      title: 'My payment failed but money was deducted',
      answer:
        'If your payment failed but the amount was deducted, it will be automatically refunded within 3-5 business days. If you don\'t receive the refund, please contact us with your transaction ID.'
    },
    {
      id: 'partner-late',
      title: 'What if the service partner is late?',
      answer:
        'If your service partner is running late, you\'ll receive a notification with the updated arrival time. Partners are usually punctual, but in case of delays due to traffic or previous bookings, we\'ll keep you informed.'
    },
    {
      id: 'service-quality',
      title: "I'm not satisfied with the service quality",
      answer:
        'We apologize for the inconvenience. Please rate your experience in the app and provide feedback. Our team will review the issue and may offer a partial refund or free re-service depending on the situation.'
    },
    {
      id: 'reschedule',
      title: 'Can I reschedule my booking?',
      answer:
        'Yes, you can reschedule your booking up to 4 hours before the scheduled time through the app or by contacting support. Rescheduling is free of charge.'
    },
    {
      id: 'track-order',
      title: 'How do I track my service request?',
      answer:
        'Once your booking is confirmed, you can track your service partner\'s location in real-time through the app. You\'ll also receive SMS updates about the status of your booking.'
    }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch(`${API_BASE_URL}/webforms/customer-support`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');

        // Reset form after a short delay
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            phone: '',
            category: '',
            orderId: '',
            message: ''
          });
          setSubmitStatus('idle');
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting support ticket:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white">
      <Navbar />

      {/* Hero Section */}
      <section
        className="py-16 bg-gradient-to-r from-purple-600 to-purple-700 text-white relative overflow-hidden"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=customer%20service%20representative%20helping%20client%20with%20smile%2C%20modern%20office%20setting%20with%20headset%20and%20computer%2C%20purple%20and%20white%20theme%2C%20professional%20customer%20support%20environment%2C%20friendly%20help%20desk%20assistance&width=1200&height=500&seq=customer-support-hero&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-purple-600/80"></div>
        <div className="container mx-auto px-6 relative z-10 text-center">
          <h1 className="text-4xl font-bold mb-4">Customer Support</h1>
          <p className="text-xl max-w-2xl mx-auto">
            We're here to help! Get quick answers to your questions or reach out to our support team
          </p>
        </div>
      </section>

      {/* Quick Contact */}
      <section className="py-8 bg-white shadow-sm">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              <a href="tel:93074 19845" className="flex items-center p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors cursor-pointer">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-phone-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Call Us</h3>
                  <p className="text-green-600 font-medium">93074 19845</p>
                </div>
              </a>

              <a href="mailto:support@baainearme.com" className="flex items-center p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors cursor-pointer">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-mail-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Email Us</h3>
                  <p className="text-blue-600 font-medium">support@baainearme.com</p>
                </div>
              </a>

              <div className="flex items-center p-4 bg-purple-50 rounded-xl cursor-pointer">
                <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-time-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Support Hours</h3>
                  <p className="text-purple-600 font-medium">24/7 Available</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Common Issues */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">Quick answers to common questions</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {commonIssues.map(issue => (
                <div key={issue.id} className="bg-white rounded-xl shadow-sm border border-gray-100">
                  <button
                    onClick={() => setSelectedTicket(selectedTicket === issue.id ? null : issue.id)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <h3 className="font-semibold text-gray-800">{issue.title}</h3>
                    <i
                      className={`ri-arrow-down-s-line text-gray-400 transition-transform ${
                        selectedTicket === issue.id ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  {selectedTicket === issue.id && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-600">{issue.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Still Need Help?</h2>
              <p className="text-xl text-gray-600">Submit a support ticket and we'll get back to you within 24 hours</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
              <form id="customer-support-form" onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    placeholder="Enter your email address"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="category" className="block text-sm font-semibold text-gray-700 mb-2">
                      Issue Category *
                    </label>
                    <div className="relative">
                      <select
                        id="category"
                        name="category"
                        required
                        value={formData.category}
                        onChange={e => setFormData(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm appearance-none bg-white"
                      >
                        <option value="">Select issue category</option>
                        {supportCategories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <i className="ri-arrow-down-s-line text-gray-400"></i>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="orderId" className="block text-sm font-semibold text-gray-700 mb-2">
                      Order ID (if applicable)
                    </label>
                    <input
                      type="text"
                      id="orderId"
                      name="orderId"
                      value={formData.orderId}
                      onChange={e => setFormData(prev => ({ ...prev, orderId: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      placeholder="Enter your order ID"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Describe Your Issue *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    maxLength={500}
                    value={formData.message}
                    onChange={e => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm resize-none"
                    placeholder="Please describe your issue in detail..."
                  ></textarea>
                  <div className="text-right text-sm text-gray-500 mt-1">{formData.message.length}/500 characters</div>
                </div>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center">
                      <i className="ri-check-line text-green-600 mr-2"></i>
                      <p className="text-green-800 font-semibold">Support ticket submitted successfully!</p>
                    </div>
                    <p className="text-green-700 text-sm mt-1">We'll get back to you within 24 hours via email or phone.</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                      <i className="ri-error-warning-line text-red-600 mr-2"></i>
                      <p className="text-red-800 font-semibold">Please fill in all required fields</p>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-purple-600 text-white py-4 rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Submitting Ticket...
                    </span>
                  ) : (
                    'Submit Support Ticket'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img
                src="https://static.readdy.ai/image/10053dadbce1851b7032d4a2f38526c3/83173065781489fd4e90ffc998d6a3d9.jfif"
                alt="Baainearme"
                className="h-8 mb-4"
              />
              <p className="text-gray-400">We're here to help you 24/7</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">For Customers</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link to="/book-services" className="hover:text-white cursor-pointer">
                    Book Services
                  </Link>
                </li>
                <li>
                  <a href="#" className="hover:text-white cursor-pointer">
                    Track Orders
                  </a>
                </li>
                <li>
                  <Link to="/customer-support" className="hover:text-white cursor-pointer">
                    Customer Support
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link to="/partner" className="hover:text-white cursor-pointer">
                    Become Partner
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="hover:text-white cursor-pointer">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/faqs" className="hover:text-white cursor-pointer">
                    FAQs
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link to="/customer-terms" className="hover:text-white cursor-pointer">
                    Terms &amp; Conditions
                  </Link>
                </li>
                <li>
                  <Link to="/customer-privacy" className="hover:text-white cursor-pointer">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/refund-policy" className="hover:text-white cursor-pointer">
                    Refund Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Baainearme. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
